import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import axios from 'axios';
import Scrollbars from 'react-custom-scrollbars';
import { AppNoInfo, AppRemixIcon } from '@/layout/AppComponents';
import ReactDOMServer from 'react-dom/server';
import Translator from '../../../../../../components/helpers/Translator';
import { axiosPath } from '../../../../../../../routing/helpers/router';
import * as currentPageAction from '../../../../../../../store/actions/currentPageAction';
import useIsMounted from '../../../../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import AppLink from '../../../../../../../routing/helpers/AppLink';
import colors from '../../../../../../../constants/colors';
import ActionPlanFields from './ActionPlanFields';
import { getActionPlanState } from '../../../tools/totalEnergiesRiskActionPlanTools';

export default function ActionPlanTab({ elementID }) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge
  );
  const actionPlans = useSelector(
    (s) => s.currentPage?.actionPlanTab?.[elementID]
  );
  const [isLoading, setIsLoading] = useState(true);
  const [selectedActionPlan, setSelectedActionPlan] = useState(null);

  // > LOAD ACTION PLAN TAB
  const loadActionPlans = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_totalEnergiesRisk_risk_actionPlan_tab"),
          { moduleBridgeHex, riskID: elementID }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("actionPlanTab", {
                [elementID]: data,
              })
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setIsLoading(false)),
    [moduleBridgeHex, elementID]
  );

  useEffect(() => {
    if (isLoading) loadActionPlans();
  }, [isLoading]);
  // < LOAD ACTION PLAN TAB

  if (isLoading) return <AppNoInfo minHeight={300} />;

  return (
    <Container>
      <SidebarMenu>
        <>
          {userBridge?.permissions?.level === 0 && (
            <AppLink
              path="user_module_totalEnergiesRisk_actionPlan_add"
              params={{
                moduleBridgeHex,
                riskID: elementID,
              }}
              className="add-button"
            >
              Nuevo plan de acción
              {` (${actionPlans?.length || 0})`}
            </AppLink>
          )}
          {actionPlans?.length > 0 ? (
            <Scrollbars style={{ height: '365px' }}>
              <ul>
                {actionPlans.length > 0 &&
                  actionPlans.map((ap) => (
                    <li
                      style={{
                        borderLeftColor:
                          colors?.totalEnergiesRiskActionPlanState[ap?.state],
                      }}
                      key={ap.id}
                      className={`action-plan ${
                        selectedActionPlan?.id === ap.id ? 'selected' : ''
                      }`}
                      title={getActionPlanState(ap?.state)}
                      onClick={() => setSelectedActionPlan(ap)}
                    >
                      <div className="first-row">{ap?.description}</div>
                    </li>
                  ))}
              </ul>
            </Scrollbars>
          ) : (
            <AppNoInfo
              text="Sin planes de acción"
              color={colors.primaryLight}
              icon={
                <AppRemixIcon
                  size={24}
                  icon="file-search"
                  color={colors.primaryLight}
                />
              }
            />
          )}
        </>
      </SidebarMenu>
      <Content>
        {selectedActionPlan ? (
          <>
            <div className="container-fluid">
              <div className="row">
                <div className="col-lg-12 d-flex justify-content-end">
                  {userBridge?.permissions?.level === 0 && (
                    <AppLink
                      className="mr-2"
                      title="Editar"
                      path="user_module_totalEnergiesRisk_actionPlan_edit"
                      params={{
                        moduleBridgeHex,
                        actionPlanHex: selectedActionPlan?.hex,
                      }}
                    >
                      <AppRemixIcon
                        icon="edit"
                        className="pointer"
                        size={16}
                        onClick={() => {
                          setSelectedActionPlan(null);
                        }}
                      />
                    </AppLink>
                  )}
                  <AppRemixIcon
                    title="Cerrar"
                    icon="close"
                    className="pointer"
                    size={16}
                    onClick={() => {
                      setSelectedActionPlan(null);
                    }}
                  />
                </div>
              </div>
            </div>
            <ActionPlanFields values={selectedActionPlan} isReadOnly />
          </>
        ) : (
          <AppNoInfo
            text="Sin plan de acción seleccionado"
            color={colors.primaryLight}
            icon={
              <AppRemixIcon
                size={24}
                icon="file-search"
                color={colors.primaryLight}
              />
            }
          />
        )}
      </Content>
    </Container>
  );
}

const Container = styled.div`
  height: 400px;
  display: grid;
  grid-template-columns: 260px 1fr;
`;

const SidebarMenu = styled.div`
  overflow-y: auto;
  padding: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  .add-button {
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid ${colors.primary};
    color: ${colors.primary};
    margin: 0;
    height: 32px;
    cursor: pointer;
  }
  ul {
    padding: 4px;
    overflow-y: auto;
    li {
      &.action-plan {
        border-left-width: 6px;
        border-left-style: solid;
        .first-row {
          padding-left: 6px;
          max-height: 40px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          width: 235px;
        }
      }
      padding: 4px 6px 4px 4px;
      border-bottom: 1px solid ${colors.grey300};
      cursor: pointer;
      &.selected {
        background: ${colors.grey300};
      }
      .first-row {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        color: ${colors.primary};
        .name {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      .second-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 10px;
        .sr-date {
          letter-spacing: 1px;
        }
        .sr-user {
          color: ${colors.primary};
        }
      }
    }
  }
`;

const Content = styled.div`
  overflow-y: auto;
  border-left: 1px solid ${colors.grey300};
  padding: 4px;
  height: 400px;
  .row-no-margin > .row {
    margin: 0;
  }
  h3 {
    font-size: 13px;
    margin: 0px 0px 6px;
    font-weight: bold;
  }
`;
